import styled, { useTheme } from "styled-components";
import {
  // New V2 token-driven components
  PrimaryButtonLargeV2,
  PrimaryButtonMediumV2,
  PrimaryButtonSmallV2,
  SecondaryButtonLargeV2,
  SecondaryButtonMediumV2,
  SecondaryButtonSmallV2,
  TertiaryButtonLargeV2,
  TertiaryButtonMediumV2,
  TertiaryButtonSmallV2,
  DestructiveButtonLargeV2,
  DestructiveButtonMediumV2,
  DestructiveButtonSmallV2,
  GhostButtonLargeV2,
  GhostButtonMediumV2,
  GhostButtonSmallV2,
  LinkButtonLargeV2,
  LinkButtonMediumV2,
  LinkButtonSmallV2,
} from "../../components/Buttons/Buttons";
import { DeleteIcon, PlusIcon } from "../../components/Icons/Icons";

const Container = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  max-width: 1200px;
  margin: 0 auto;
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.resolve("font_size_xl")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export function ButtonTestPage() {
  const theme = useTheme();
  return (
    <Container>
      <h1>Buttons</h1>

      <Section>
        <SectionTitle>Primary Buttons </SectionTitle>
        <ButtonGroup>
          <ButtonContainer>
            <PrimaryButtonLargeV2>Large</PrimaryButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <PrimaryButtonMediumV2>Medium</PrimaryButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <PrimaryButtonSmallV2>Small</PrimaryButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
        <ButtonGroup>
          <ButtonContainer>
            <PrimaryButtonLargeV2>
              <PlusIcon />
              Large (Auto-sized 16px icon)
            </PrimaryButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <PrimaryButtonMediumV2>
              <PlusIcon />
              Medium (Auto-sized 14px icon)
            </PrimaryButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <PrimaryButtonSmallV2>
              <PlusIcon />
              Small (Auto-sized 12px icon)
            </PrimaryButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>Secondary Buttons </SectionTitle>
        <ButtonGroup>
          <ButtonContainer>
            <SecondaryButtonLargeV2>Large</SecondaryButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <SecondaryButtonMediumV2>Medium</SecondaryButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <SecondaryButtonSmallV2>Small</SecondaryButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
        <ButtonGroup>
          <ButtonContainer>
            <SecondaryButtonLargeV2>
              <PlusIcon />
              Large
            </SecondaryButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <SecondaryButtonMediumV2>
              <PlusIcon />
              Medium
            </SecondaryButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <SecondaryButtonSmallV2>
              <PlusIcon />
              Small
            </SecondaryButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>Tertiary Buttons </SectionTitle>
        <ButtonGroup>
          <ButtonContainer>
            <TertiaryButtonLargeV2>Large</TertiaryButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <TertiaryButtonMediumV2>Medium</TertiaryButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <TertiaryButtonSmallV2>Small</TertiaryButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
        <ButtonGroup>
          <ButtonContainer>
            <TertiaryButtonLargeV2>
              <PlusIcon />
              Large
            </TertiaryButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <TertiaryButtonMediumV2>
              <PlusIcon />
              Medium
            </TertiaryButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <TertiaryButtonSmallV2>
              <PlusIcon />
              Small
            </TertiaryButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>Button States</SectionTitle>
        <ButtonGroup>
          <ButtonContainer>
            <PrimaryButtonMediumV2>Normal</PrimaryButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <PrimaryButtonMediumV2 disabled>Disabled</PrimaryButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <PrimaryButtonMediumV2 loading>Loading</PrimaryButtonMediumV2>
          </ButtonContainer>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>
          Full Width Buttons (PrimaryButtonFitContainer Replacement)
        </SectionTitle>
        <ButtonGroup style={{ flexDirection: "column", gap: "12px" }}>
          <PrimaryButtonLargeV2 fullWidth>
            <PlusIcon />
            Full Width Large Button
          </PrimaryButtonLargeV2>
          <PrimaryButtonMediumV2 fullWidth>
            <PlusIcon />
            Full Width Medium Button
          </PrimaryButtonMediumV2>
          <PrimaryButtonSmallV2 fullWidth>
            <PlusIcon />
            Full Width Small Button
          </PrimaryButtonSmallV2>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>Destructive Buttons</SectionTitle>
        <ButtonGroup>
          <ButtonContainer>
            <DestructiveButtonLargeV2>Large</DestructiveButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <DestructiveButtonMediumV2>Medium</DestructiveButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <DestructiveButtonSmallV2>Small</DestructiveButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
        <ButtonGroup>
          <ButtonContainer>
            <DestructiveButtonLargeV2>
              <DeleteIcon fill={theme.resolve("color_icon_error")} />
              Large
            </DestructiveButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <DestructiveButtonMediumV2>
              <DeleteIcon fill={theme.resolve("color_icon_error")} />
              Medium
            </DestructiveButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <DestructiveButtonSmallV2>
              <DeleteIcon fill={theme.resolve("color_icon_error")} />
              Small
            </DestructiveButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>Ghost Buttons</SectionTitle>
        <ButtonGroup>
          <ButtonContainer>
            <GhostButtonLargeV2>Large</GhostButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <GhostButtonMediumV2>Medium</GhostButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <GhostButtonSmallV2>Small</GhostButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
        <ButtonGroup>
          <ButtonContainer>
            <GhostButtonLargeV2>
              <PlusIcon />
              Large
            </GhostButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <GhostButtonMediumV2>
              <PlusIcon />
              Medium
            </GhostButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <GhostButtonSmallV2>
              <PlusIcon />
              Small
            </GhostButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>Link Buttons</SectionTitle>
        <ButtonGroup>
          <ButtonContainer>
            <LinkButtonLargeV2>Large</LinkButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <LinkButtonMediumV2>Medium</LinkButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <LinkButtonSmallV2>Small</LinkButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
        <ButtonGroup>
          <ButtonContainer>
            <LinkButtonLargeV2>
              <PlusIcon />
              Large
            </LinkButtonLargeV2>
          </ButtonContainer>
          <ButtonContainer>
            <LinkButtonMediumV2>
              <PlusIcon />
              Medium
            </LinkButtonMediumV2>
          </ButtonContainer>
          <ButtonContainer>
            <LinkButtonSmallV2>
              <PlusIcon />
              Small
            </LinkButtonSmallV2>
          </ButtonContainer>
        </ButtonGroup>
      </Section>
    </Container>
  );
}
